/* @flow */
import * as React from 'react';
import { Route } from 'react-router-dom';
import { translate } from 'react-i18next';

import { Title } from 'common/components/common';
import Products from 'common/enums/Product';

import { EntityMgmtHeader } from 'common/containers/AppHeaderContainer';

import SectionWrap from 'common/components/common/SectionWrap';

import withRouteTracker from 'common/middleware/RouteTracker';
import {
    withRoutePermissionCheck,
    RoleOperationEnum
} from 'common/modules/rbac';

import { SettingsRoute } from 'container/containers/settings';
import { type GetRoutesFn } from './types';
import { lazy } from 'common/modules/mfe';

const SMSConfiguration = lazy(() =>
    import('trackiq-app/settings/SMSConfiguration')
);
const SlotAttributeConfiguration = lazy(() =>
    import('trackiq-app/settings/SlotAttributeConfiguration')
);
const ChecklistConfiguration = lazy(() =>
    import('trackiq-app/settings/ChecklistConfiguration')
);
const BannerConfiguration = lazy(() =>
    import('trackiq-app/settings/BannerConfiguration')
);

const DispatchWaveConfiguration = lazy(() =>
    import('orderiq-app/settings/DispatchWaveConfiguration')
);

const RuleConfiguration = lazy(() =>
    import('orchestrator-app/settings/RuleSetContainer')
);

const TrackedSMSConfiguration = withRouteTracker(
    withRoutePermissionCheck(
        translate()(props => (
            <SectionWrap>
                <Title i18nKey="smsConfigTitle" />
                <EntityMgmtHeader {...props} />
                <SMSConfiguration {...props} />
            </SectionWrap>
        )),
        RoleOperationEnum.READ_SMS_CONFIGURATION
    ),
    Products.MOTION_TRACK
);

const TrackedChecklistConfiguration = withRouteTracker(
    withRoutePermissionCheck(
        translate()(props => (
            <SectionWrap>
                <Title i18nKey="checklistConfigTitle" />
                <EntityMgmtHeader {...props} />
                <ChecklistConfiguration {...props} />
            </SectionWrap>
        )),
        RoleOperationEnum.READ_CHECKLIST_CONFIGURATION
    ),
    Products.MOTION_TRACK
);

const TrackedBannerConfiguration = withRouteTracker(
    translate()(props => (
        <SectionWrap>
            <Title i18nKey="bannerConfigTitle" />
            <EntityMgmtHeader {...props} />
            <BannerConfiguration {...props} />
        </SectionWrap>
    )),
    Products.MOTION_TRACK
);

const TrackedDispatchWaveConfiguration = withRouteTracker(
    withRoutePermissionCheck(
        translate()(props => (
            <SectionWrap>
                <Title i18nKey="dispatchWaveTitle" />
                <DispatchWaveConfiguration {...props} />
            </SectionWrap>
        )),
        RoleOperationEnum.UPDATE_ORDER_IQ_SETTINGS
    ),
    Products.ORCHESTRATOR
);

const TrackedShipflexRuleConfiguration = withRouteTracker(
    withRoutePermissionCheck(
        translate()(props => (
            <SectionWrap>
                <Title i18nKey={'settings'} />
                <RuleConfiguration {...props} />
            </SectionWrap>
        )),
        RoleOperationEnum.UPDATE_PARCEL_ORCHESTRATION_SETTINGS
    ),
    Products.ORCHESTRATION
);

const getRoutes: GetRoutesFn = ({
    visibleSections,
    match,
    isGlobalPersonnel
}) => {
    return [
        isGlobalPersonnel && visibleSections.showLiveViewSection && (
            <Route
                path={`${match.path}/settings/trackIQ/sms`}
                component={TrackedSMSConfiguration}
            />
        ),
        visibleSections.showLiveViewSection && (
            <Route
                path={`${match.path}/settings/trackIQ/checklist`}
                component={TrackedChecklistConfiguration}
            />
        ),
        visibleSections.showLiveViewSection && (
            <Route
                path={`${match.path}/settings/trackIQ/banners`}
                component={TrackedBannerConfiguration}
            />
        ),
        <Route
            path={`${match.path}/settings/trackIQ/slot-attribute-settings`}
            component={SlotAttributeConfiguration}
            key={`${match.path}/settings/trackIQ/slot-attribute-settings`}
        />,
        <Route
            path={`${match.path}/settings/orderIQ/dispatch-wave`}
            key={`${match.path}/settings/orderIQ/dispatch-wave`}
            component={TrackedDispatchWaveConfiguration}
        />,
        <Route
            path={`${match.path}/settings/shipflex/rules`}
            key={`${match.path}/settings/shipflex/rules`}
            component={TrackedShipflexRuleConfiguration}
        />,
        <Route
            path={`${match.path}/settings`}
            component={SettingsRoute}
            key={`${match.path}/settings`}
        />
    ].filter(Boolean);
};

export default getRoutes;
